import React from 'react';
import { ToggleSection } from '@/components/form/ToggleSection';
import { FeeAmountFieldset } from './FeeAmountFieldset';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { NumberInputRow } from '@/components/form/NumberInputRow';

interface LateFeeSectionProps {
  localUseLateFee: boolean;
  localLateFeeType: 'percentage' | 'fixed';
  recurringLateFee: string;
  lateFeePctProps?: Record<string, unknown>;
  lateFeePctError?: React.ReactNode;
  lateFeeFixedProps?: Record<string, unknown>;
  lateFeeFixedError?: React.ReactNode;
  recurringIntervalProps?: Record<string, unknown>;
  recurringIntervalError?: React.ReactNode;
  onToggleLateFee: (checked: boolean) => void;
  onLateFeeTypeChange: (type: 'percentage' | 'fixed') => void;
  onRecurringLateFeeChange: (value: string) => void;
  onIsRecurringLateFeeChange: (checked: boolean) => void;
}

export const LateFeeSection: React.FC<LateFeeSectionProps> = ({
  localUseLateFee,
  localLateFeeType,
  recurringLateFee,
  lateFeePctProps,
  lateFeePctError,
  lateFeeFixedProps,
  lateFeeFixedError,
  recurringIntervalProps,
  recurringIntervalError,
  onToggleLateFee,
  onLateFeeTypeChange,
  onRecurringLateFeeChange,
  onIsRecurringLateFeeChange,
}) => {
  return (
    <ToggleSection
      id="useLateFee"
      label="Apply Late Fee"
      description="Charge clients for overdue invoices"
      checked={localUseLateFee}
      onCheckedChange={onToggleLateFee}
      variant="indented"
    >
      <FeeAmountFieldset
        type={localLateFeeType}
        onTypeChange={onLateFeeTypeChange}
        percentageInputProps={lateFeePctProps}
        percentageErrorElement={lateFeePctError}
        fixedInputProps={lateFeeFixedProps}
        fixedErrorElement={lateFeeFixedError}
        idPrefix="late"
        percentageLabel={<span className="whitespace-nowrap">Late Fee Percentage</span>}
        fixedLabel={<span className="whitespace-nowrap">Late Fee Amount</span>}
      />
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor="isRecurringLateFee" className="cursor-pointer text-foreground">
              Recurring Late Fee
            </Label>
            <p className="text-xs text-muted-foreground">
              Apply the late fee multiple times
            </p>
          </div>
          <Switch
            id="isRecurringLateFee"
            checked={recurringLateFee !== 'once'}
            onCheckedChange={onIsRecurringLateFeeChange}
          />
        </div>
        {recurringLateFee !== 'once' && (
          <RadioGroup
            value={recurringLateFee}
            onValueChange={onRecurringLateFeeChange}
            className="flex gap-4 mt-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="daily" id="rf-daily" />
              <Label htmlFor="rf-daily">Daily</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="interval" id="rf-interval" />
              <Label htmlFor="rf-interval">Regular Interval</Label>
            </div>
          </RadioGroup>
        )}
        {recurringLateFee === 'interval' && (
          <NumberInputRow
            id="recurringLateFeeInterval"
            label="Recurring Late Fee Interval (Days)"
            noWrap
            description="How often to apply the late fee"
            unit="days"
            unitPosition="right"
            placeholder="e.g. 7"
            inputProps={recurringIntervalProps ?? {}}
            errorElement={recurringIntervalError}
            className="animate-fade-in"
          />
        )}
      </div>
    </ToggleSection>
  );
};
